import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOMeta from '@/components/feature/SEOMeta';
import { products, categories } from '@/mocks/products';
import type { Product } from '@/mocks/products';

export default function CategoryPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [filteredProducts, setFilteredProducts] = useState<Product[]>(products);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (activeCategory === 'all') {
      setFilteredProducts(products);
    } else {
      setFilteredProducts(products.filter((p) => p.category === activeCategory));
    }
  }, [activeCategory]);

  const categoryName = categories.find((c) => c.id === activeCategory)?.name || '全部';

  return (
    <>
      <SEOMeta
        title={`${categoryName}成人精品 | 臻品阁分类浏览`}
        description={`臻品阁${categoryName}成人精品分类，精选全球高品质成人精品产品。安全私密配送，品质保障，满足您的多元需求。`}
        keywords={`成人精品,${categoryName},成人精品推荐,高品质成人精品,成人精品购买`}
        canonicalUrl="https://www.example.com/category"
      />

      {/* BreadcrumbList Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.example.com/' },
              { '@type': 'ListItem', position: 2, name: '分类浏览', item: 'https://www.example.com/category' },
            ],
          }),
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 md:pt-24">
          {/* Page Header */}
          <section className="w-full py-10 md:py-14 px-6 md:px-10 bg-background-100/50">
            <div className="max-w-7xl mx-auto">
              {/* Breadcrumb */}
              <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line" />
                <span className="text-foreground-700">分类浏览</span>
                {activeCategory !== 'all' && (
                  <>
                    <i className="ri-arrow-right-s-line" />
                    <span className="text-foreground-900 font-medium">{categoryName}</span>
                  </>
                )}
              </nav>
              <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900">
                成人精品分类浏览
              </h1>
              <p className="mt-2 text-sm text-foreground-600">
                探索我们的精选产品系列，找到最适合您的品质之选
              </p>
            </div>
          </section>

          {/* Category Tabs */}
          <section className="w-full px-6 md:px-10 py-6 sticky top-16 md:top-20 z-40 bg-background-50/95 backdrop-blur-md border-b border-background-200/50">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-2 overflow-x-auto pb-1 flex-wrap">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    className={`flex items-center gap-1.5 px-4 py-2 text-sm font-label whitespace-nowrap rounded-full transition-all duration-300 cursor-pointer ${
                      activeCategory === cat.id
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${cat.icon} text-xs`} />
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Product Grid */}
          <section className="w-full py-8 md:py-12 px-6 md:px-10">
            <div className="max-w-7xl mx-auto">
              <p className="text-sm text-foreground-500 mb-6">
                共 <strong className="text-foreground-900">{filteredProducts.length}</strong> 款精品
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-product-shop="">
                {filteredProducts.map((product) => (
                  <div key={product.id} className="group cursor-pointer">
                    <div className="relative aspect-square rounded-lg overflow-hidden bg-background-100 mb-3">
                      <img
                        src={product.image}
                        alt={product.name}
                        title={`${product.name} - 成人精品推荐`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {product.badge && (
                        <span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] font-label font-medium rounded-full bg-primary-500 text-background-50">
                          {product.badge}
                        </span>
                      )}
                      {product.originalPrice && (
                        <span className="absolute top-2 right-2 px-2 py-0.5 text-[10px] font-label rounded-full bg-background-50/90 text-primary-700">
                          省¥{product.originalPrice - product.price}
                        </span>
                      )}
                    </div>
                    <h3 className="text-sm font-label font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors">
                      {product.name}
                    </h3>
                    <div className="flex items-center gap-1 mt-1">
                      <i className="ri-star-fill text-[10px] text-accent-500" />
                      <span className="text-xs text-foreground-600">{product.rating}</span>
                      <span className="text-xs text-foreground-400">({product.reviewCount})</span>
                    </div>
                    <p className="text-xs text-foreground-500 mt-1 line-clamp-1">{product.description}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-base font-heading font-semibold text-primary-600">
                        ¥{product.price}
                      </span>
                      {product.originalPrice && (
                        <span className="text-xs text-foreground-400 line-through">
                          ¥{product.originalPrice}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {filteredProducts.length === 0 && (
                <div className="text-center py-20">
                  <i className="ri-inbox-line text-4xl text-foreground-300" />
                  <p className="mt-3 text-foreground-500">该分类暂无产品</p>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}